import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { apiFetch, apiFetchJson } from '../../apiClient';
import './EmailPreferences.css';

function EmailPreferences() {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchJson(instance, '/api/EmailJobs/preferences');
      const list = Array.isArray(data?.types) ? data.types : (data?.types?.$values || []);
      setTypes(list.filter(Boolean));
    } catch (err) {
      console.error('Failed to load email preferences:', err);
      setError(t('email.loadError'));
    }
    setLoading(false);
  }, [instance, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (type, enabled) => {
    // Optimistic: the switch should feel instant, and we reload on failure anyway.
    setTypes(prev => prev.map(x => (x.type === type ? { ...x, enabled } : x)));
    try {
      await apiFetchJson(instance, '/api/EmailJobs/preferences', {
        method: 'PUT',
        body: JSON.stringify({ type, enabled })
      });
    } catch (err) {
      console.error('Failed to save email preference:', err);
      load();
    }
  };

  const preview = async (type) => {
    setBusy(`preview-${type}`);
    setStatus(null);
    try {
      // The endpoint needs a bearer token, so it can't just be opened in a new tab.
      const res = await apiFetch(instance, `/api/EmailJobs/preview/${type}`);
      const html = await res.text();
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to preview email:', err);
      setStatus({ kind: 'error', text: t('email.previewError') });
    }
    setBusy(null);
  };

  const sendNow = async (type) => {
    setBusy(`send-${type}`);
    setStatus(null);
    try {
      const result = await apiFetchJson(instance, `/api/EmailJobs/send/${type}`, { method: 'POST' });
      if (result?.sent > 0) {
        setStatus({ kind: 'ok', text: t('email.sendOk') });
      } else if (result?.emailConfigured === false) {
        setStatus({ kind: 'error', text: t('email.notConfigured') });
      } else {
        setStatus({ kind: 'muted', text: t('email.nothingToSend') });
      }
    } catch (err) {
      console.error('Failed to send email:', err);
      setStatus({ kind: 'error', text: t('email.sendError') });
    }
    setBusy(null);
  };

  if (loading) return <div className="email-prefs-loading">{t('common.loading')}</div>;
  if (error) {
    return (
      <div className="email-prefs">
        <p className="email-prefs-error">{error}</p>
        <button className="email-prefs-retry" onClick={load}>{t('common.retry')}</button>
      </div>
    );
  }

  return (
    <div className="email-prefs">
      <h2 className="email-prefs-title">{t('email.title')}</h2>
      <p className="email-prefs-intro">{t('email.intro')}</p>

      {types.map(item => (
        <div className="email-pref" key={item.type}>
          <div className="email-pref-main">
            <label className="email-pref-switch">
              <input
                type="checkbox"
                checked={!!item.enabled}
                onChange={e => toggle(item.type, e.target.checked)}
              />
              <span className="email-pref-slider" aria-hidden="true" />
            </label>
            <div className="email-pref-text">
              <span className="email-pref-label">{t(`email.types.${item.type}.label`, item.label)}</span>
              <span className="email-pref-desc">{t(`email.types.${item.type}.desc`, '')}</span>
            </div>
          </div>
          <div className="email-pref-actions">
            <button
              className="email-pref-btn"
              disabled={busy === `preview-${item.type}`}
              onClick={() => preview(item.type)}
            >
              {busy === `preview-${item.type}` ? t('common.loading') : t('email.preview')}
            </button>
            <button
              className="email-pref-btn"
              disabled={busy === `send-${item.type}` || !item.enabled}
              onClick={() => sendNow(item.type)}
            >
              {busy === `send-${item.type}` ? t('email.sending') : t('email.sendNow')}
            </button>
          </div>
        </div>
      ))}

      {status && <p className={`email-prefs-status ${status.kind}`}>{status.text}</p>}
    </div>
  );
}

export default EmailPreferences;
